<!--
  REDEEM-02 实物兑换弹窗:完整状态机 F-1 → F-2 → F-3 → F-4 → F-success / F-5
  共享子组件(P04):PanelModal / FormItem / ConfirmSheet / SuccessSheet / SoldOutPopup
  共享 composable(P04):useRedeemForm(shared 包内,injection 入 apiClient 调用)

  P03 契约(**props + emits 不得修改**):
    props { visible, resultPublicId, prizeTitle?, submit? }
    emits { close, redemption-complete, share-success }

  D-24 约束:本文件禁止 apiClient / mets 业务契约类型。
  apiClient.redeemPhysical 的调用由父容器(user-prize-list/render/index.vue)
  以 `submit` prop 注入,本组件只驱动状态机。
-->
<template>
  <!-- Vue 2.6 要求单根;多态 PanelModal 用 v-if 同时只渲染一个,逻辑上仍然单 modal -->
  <div class="redeem-physical-modal-root">
    <!-- F-1 / F-2 / F-3:填表面板 -->
    <PanelModal
      v-if="visible && isFormPanelVisible"
      :visible="true"
      :page-type="pageType"
      :content-padded="false"
      :show-close-button="pageType === 'PC'"
      @close="onPanelClose"
    >
    <!-- F-1/F-2/F-3:填表(同模板,用 errors 驱动红边 + canSubmit 驱动按钮态) -->
    <div class="fill-form" :class="`page-${pageType}`">
      <h2 class="title">{{ t('Modal.fillTitle') }}</h2>
      <p class="subtitle">{{ t('Modal.fillSubtitle') }}</p>
      <FormItem
        class="first-form-item"
        :label="t('Modal.nameLabel')"
        :value="form.name.value"
        :placeholder="t('Modal.namePlaceholder')"
        :error="form.errors.value.name"
        :page-type="pageType"
        @update:value="onNameInput"
        @blur="onNameBlur"
      />
      <FormItem
        :label="t('Modal.emailLabel')"
        :value="form.email.value"
        :placeholder="t('Modal.emailPlaceholder')"
        :error="form.errors.value.email"
        type="email"
        :page-type="pageType"
        @update:value="onEmailInput"
        @blur="onEmailBlur"
      />
      <div class="cta">
        <button
          type="button"
          class="btn-ghost"
          @click="onPanelClose"
        >
          {{ t('Modal.back') }}
        </button>
        <button
          type="button"
          class="btn-primary"
          :class="{ disabled: !form.canSubmit.value }"
          :disabled="!form.canSubmit.value"
          @click="onConfirmFromForm"
        >
          {{ t('Modal.confirm') }}
        </button>
      </div>
      <div
        v-if="pageType === 'H5'"
        class="home-indicator"
        aria-hidden="true"
      />
    </div>

    <!-- F-4 已改为独立 NoticeModal,这里只保留填表面板 -->
    </PanelModal>

  <!-- 二次确认 / 售空:居中 NoticeModal,保持单实例避免 teleport 切换时 DOM 失配 -->
  <NoticeModal
    v-if="visible && isNoticeStep"
    :visible="true"
    :page-type="pageType"
    :variant="noticeVariant"
    :text-align="noticeTextAlign"
    :title="noticeTitle"
    :description="noticeDescription"
    :button-text="noticeButtonText"
    @close="onNoticeClose"
  >
    <ConfirmSheet
      v-if="isConfirmStep"
      :page-type="pageType"
      variant="physical"
      :subtitle="t('Modal.confirmSubtitlePhysical')"
      :rows="confirmRows"
      :embedded="true"
      :show-header="false"
      @edit="onEdit"
      @confirm="form.handleConfirm()"
    />
  </NoticeModal>

  <!-- F-success:独立 PanelModal(推翻 D-16) -->
  <PanelModal
    v-if="visible && isSuccessStep"
    :visible="true"
    :page-type="pageType"
    :content-padded="false"
    :panel-padding="false"
    :show-indicator="false"
    :show-home-indicator="true"
    :show-close-button="pageType === 'PC'"
    :panel-width="pageType === 'PC' ? '580px' : ''"
    :panel-background="pageType === 'PC' ? 'transparent' : '#ffffff'"
    @close="onSuccessClose"
  >
    <SuccessSheet
      :page-type="pageType"
      variant="physical"
      :prize-title="prizeTitle"
      :rows="successRows"
      subtitle-key="successSubtitlePhysical"
      text-align="left"
      @close="onSuccessClose"
      @share-friends="onShareFriends"
    />
  </PanelModal>

  </div>
</template>

<script lang="ts">
import { computed } from 'vue-demi';
// Vue2 仓内惯例:require(...).use 避免 ESM interop

import PanelModal from './PanelModal.vue';
import NoticeModal from './NoticeModal.vue';
import FormItem from './FormItem.vue';
import ConfirmSheet from './ConfirmSheet.vue';
import SuccessSheet from './SuccessSheet.vue';
import { useRedeemForm } from '../composables/useRedeemForm';
import t from '../utils/i18n';

/**
 * submit 签名由父容器适配;shared 只产出表单字段,seasonSign 由父容器补齐。
 * 这里写行内类型避免 import @ark/mets-shared(D-24)
 */
type PhysicalSubmitFn = (req: {
  resultPublicId: string;
  contactInfo: {
    name: string;
    email: string;
  };
}) => Promise<{
  success: boolean;
}>;
type Props = {
  visible: boolean;
  resultPublicId: string;
  /** F-4/F-success 展示用,由父容器(P03 index.vue)onFillOut 记录并传入 */
  prizeTitle?: string;
  /**
   * P04 新增:由父容器注入的 submit 闭包(包装 apiClient.redeemPhysical)
   * D-24 强约束:本组件禁 import apiClient;调用点由 user-prize-list 的 index.vue 注入
   * 可选:未传时 handleConfirm 抛错(开发期守门)
   */
  submit?: PhysicalSubmitFn;
};
const __sfc_main = {};
__sfc_main.props = {
  visible: {
    key: "visible",
    required: true,
    type: Boolean
  },
  resultPublicId: {
    key: "resultPublicId",
    required: true,
    type: String
  },
  prizeTitle: {
    key: "prizeTitle",
    required: false,
    type: String,
    default: ''
  },
  submit: {
    key: "submit",
    required: false,
    type: null,
    default: undefined
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const {
    usePageType
  } = (require('@fegobull/webapi') as any).use;
  const props = __props;
  const emit = __ctx.emit;
  const {
    isPC
  } = usePageType() as any;
  const pageType = computed<'PC' | 'H5'>(() => {
    const isPcValue = isPC && typeof isPC === 'object' && 'value' in isPC ? isPC.value : isPC;
    return isPcValue ? 'PC' : 'H5';
  });

  // 注入 submit 闭包;adapter 从 composable form state 构造 payload 再调父 prop
  // 闭包惰性读 form.name/email(handleConfirm 时才求值,form 变量此时已定义)
  const form = useRedeemForm({
    submit: async () => {
      if (!props.submit) {
        throw new Error('RedeemPhysicalModal: `submit` prop is required at runtime. ' + 'user-prize-list/render/index.vue must pass apiClient.redeemPhysical closure.');
      }
      return props.submit({
        resultPublicId: props.resultPublicId,
        contactInfo: {
          name: form.name.value.trim(),
          email: form.email.value.trim()
        }
      });
    }
  });

  // F-1/F-2/F-3 同模板(errors / canSubmit 驱动差异)
  const isFillStep = computed(() => form.step.value === 'form-input' || form.step.value === 'form-invalid' || form.step.value === 'form-valid');
  const isConfirmStep = computed(() => form.step.value === 'confirm');
  const isSuccessStep = computed(() => form.step.value === 'success');
  const isSoldOutStep = computed(() => form.step.value === 'sold-out');
  const isNoticeStep = computed(() => isConfirmStep.value || isSoldOutStep.value);
  const isFormPanelVisible = computed(() => isFillStep.value || isConfirmStep.value || isSoldOutStep.value);
  const noticeVariant = computed<'confirm' | 'alert'>(() => isSoldOutStep.value ? 'alert' : 'confirm');
  const noticeTextAlign = computed<'left' | 'center'>(() => isSoldOutStep.value ? 'center' : 'left');
  const noticeTitle = computed(() => isSoldOutStep.value ? t('Modal.soldOutTitle') : t('Modal.confirmTitle'));
  const noticeDescription = computed(() => isSoldOutStep.value ? t('Modal.soldOutSubtitle') : t('Modal.confirmSubtitlePhysical'));
  const noticeButtonText = computed(() => isSoldOutStep.value ? t('Modal.ok') : '');
  const confirmRows = computed(() => [{
    label: t('Modal.rowLabelPrize'),
    value: props.prizeTitle
  }, {
    label: t('Modal.rowLabelName'),
    value: form.name.value.trim()
  }, {
    label: t('Modal.rowLabelEmail'),
    value: form.email.value.trim()
  }]);
  const successRows = computed(() => [{
    label: t('Modal.rowLabelPrize'),
    value: props.prizeTitle
  }, {
    label: t('Modal.rowLabelName'),
    value: form.name.value.trim()
  }, {
    label: t('Modal.rowLabelEmail'),
    value: form.email.value.trim()
  }]);
  function onNameInput(v: string): void {
    form.setFieldValue('name', v);
  }
  function onEmailInput(v: string): void {
    form.setFieldValue('email', v);
  }
  function onNameBlur(): void {
    form.validateField('name');
  }
  function onEmailBlur(): void {
    form.validateField('email');
  }
  function onConfirmFromForm(): void {
    // 校验通过 → 二次确认;失败 → inline error(由 validate 内部 setStep)
    if (form.validate()) {
      form.setStep('confirm');
    }
  }
  function onEdit(): void {
    // Edit 回填表态(Figma 要求,字段值保留;对应 UI-SPEC §4.6)
    form.setStep('form-input');
  }
  function onNoticeClose(): void {
    if (isSoldOutStep.value) {
      onSoldOutOk();
      return;
    }
    onEdit();
  }
  function onPanelClose(): void {
    emit('close');
  }
  function onSuccessClose(): void {
    // UI-SPEC §4.8.c:Close → redemption-complete(宿主刷新列表)+ close(关弹窗)
    emit('redemption-complete', {
      resultPublicId: props.resultPublicId,
      prizeType: 'physical'
    });
    emit('close');
  }
  function onShareFriends(): void {
    // UI-SPEC §4.8.c:Share to friends 纯 emit,不关 F-success(用户可继续 Close)
    emit('share-success', {
      resultPublicId: props.resultPublicId,
      prizeType: 'physical'
    });
  }
  function onSoldOutOk(): void {
    // OK 回填表态,字段保留(用户可能换 email 重试或主动 Back 放弃)
    form.setStep('form-input');
  }
  return {
    t,
    pageType,
    form,
    isConfirmStep,
    isSuccessStep,
    isNoticeStep,
    isFormPanelVisible,
    noticeVariant,
    noticeTextAlign,
    noticeTitle,
    noticeDescription,
    noticeButtonText,
    confirmRows,
    successRows,
    onNameInput,
    onEmailInput,
    onNameBlur,
    onEmailBlur,
    onConfirmFromForm,
    onEdit,
    onNoticeClose,
    onPanelClose,
    onSuccessClose,
    onShareFriends
  };
};
__sfc_main.components = Object.assign({
  PanelModal,
  FormItem,
  NoticeModal,
  ConfirmSheet,
  SuccessSheet
}, __sfc_main.components);
export default __sfc_main;
</script>

<style scoped>
/* ─── 填表区通用 ──────────────────────────────────────── */
.fill-form {
  display: flex;
  flex-direction: column;
}
.btn-primary {
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding: 0;
  background: #ff6900;
  color: #ffffff;
  border: none;
  cursor: pointer;
  font-family: BR Hendrix, Arial, sans-serif;
}
.btn-primary.disabled,
.btn-primary:disabled {
  background: #d7d7d7;
  cursor: not-allowed;
}
.btn-ghost {
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding: 0;
  background: transparent;
  color: #1d1d1d;
  cursor: pointer;
  font-family: BR Hendrix, Arial, sans-serif;
}
.cta {
  display: flex;
  justify-content: space-between;
}

/* ─── H5(rem,设计稿 100px=1rem)UI-SPEC §4.3 ──────────── */
.fill-form {
  padding: 0;
  gap: 0;
}
.fill-form .title {
  font-weight: 600;
  font-size: 0.5rem;
  line-height: 1.1em;
  letter-spacing: -0.02em;
  color: #000000;
  margin: 0;
  padding: 0.26rem 0.4rem 0;
}
.fill-form .subtitle {
  font-weight: 400;
  font-size: 0.3rem;
  line-height: 1.4em;
  color: #626262;
  margin: 0.24rem 0.4rem 0;
  text-align: left;
}
.fill-form .cta {
  padding: 0.2308rem 0.4rem;
  gap: 0.1923rem;
}
.fill-form .btn-ghost {
  width: 2.04rem;
  height: 0.9rem;
  border-radius: 2.24rem;
  border: 2px solid #1d1d1d;
  font-weight: 600;
  font-size: 0.36rem;
}
.fill-form .btn-primary {
  width: 4.54rem;
  height: 0.9rem;
  border-radius: 0.8rem;
  font-weight: 600;
  font-size: 0.36rem;
}
.fill-form .home-indicator {
  height: 0.32rem;
}

/* ─── PC 覆盖 UI-SPEC §4.3 ────────────────────────────── */
.fill-form.page-PC {
  padding: 0;
  gap: 20px;
  width: 100%;
}
.fill-form.page-PC .title {
  font-size: 24px;
  font-weight: 600;
  line-height: 1.2;
  padding: 0;
  margin: 0;
  text-align: left;
  letter-spacing: -0.02em;
  color: #000000;
}
.fill-form.page-PC .subtitle {
  font-size: 18px;
  line-height: 1.4;
  margin: -16px 0 0 0;
  color: #626262;
  text-align: left;
}
.fill-form.page-PC .cta {
  justify-content: center;
  gap: 10px;
  margin-top: 0;
  padding: 0;
}
.fill-form.page-PC .btn-ghost {
  width: 160px;
  height: 60px;
  border-radius: 60px;
  border: 1px solid #1d1d1d;
  font-size: 24px;
  font-weight: 600;
  color: #1d1d1d;
}
.fill-form.page-PC .btn-primary {
  width: 280px;
  height: 60px;
  border-radius: 60px;
  font-size: 24px;
  font-weight: 600;
}
</style>
